import { useState, useEffect } from "react";

// react-router components
import { useLocation, Link, useNavigate } from "react-router-dom";

// prop-types is a library for typechecking of props.
import PropTypes from "prop-types";

// @material-ui core components
import AppBar from "@mui/material/AppBar";
import Toolbar from "@mui/material/Toolbar";
import IconButton from "@mui/material/IconButton";
import Menu from "@mui/material/Menu";
import Icon from "@mui/material/Icon";

// Soft UI Dashboard React components
import SuiBox from "components/SuiBox";
import SuiTypography from "components/SuiTypography";
import SuiInput from "components/SuiInput";

// Soft UI Dashboard React examples
import Breadcrumbs from "examples/Breadcrumbs";
import NotificationItem from "examples/Items/NotificationItem";

// Custom styles for DashboardNavbar
import {
  navbar,
  navbarContainer,
  navbarRow,
  navbarIconButton,
  navbarMobileMenu,
} from "examples/Navbars/DashboardNavbar/styles";

// Soft UI Dashboard React context
import {
  useSoftUIController,
  setTransparentNavbar,
  setMiniSidenav,
  setOpenConfigurator,
} from "context";

// Images
import team2 from "assets/images/team-2.jpg";
import logoSpotify from "assets/images/small-logos/logo-spotify.svg";
import { useDispatch, useSelector } from "react-redux";
import { signout } from "layouts/authentication/auth.reducer";
import CustomSelect from "components/CustomSelect";
import { CircularProgress } from "@mui/material";
import { updateDefaultTeamChannel } from "layouts/authentication/auth.reducer";
import { customSelectStyles } from "./styles";

function DashboardNavbar({ absolute, light, isMini, breadcrumbTitle }) {
  const { isLoggedIn, admin, team, channel, admin_teams_channels } = useSelector(
    (state) => state.auth
  );
  const [navbarType, setNavbarType] = useState();
  const [controller, dispatch] = useSoftUIController();
  const { miniSidenav, transparentNavbar, fixedNavbar, openConfigurator } = controller;
  const [openMenu, setOpenMenu] = useState(false);
  const route = useLocation().pathname.split("/").slice(1);
  const reactDispatch = useDispatch();
  const [teamsChannels, setTeamsChannels] = useState([]);
  const [selectedTeamChannel, setSelectedTeamChannel] = useState({});
  const [isLoading, setIsLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    setIsLoading(true);
    setTeamsChannels(
      admin_teams_channels.map((data) => {
        return {
          label: `${data.team_name} - ${data.channel_name}`,
          value: data.channel_id,
          team: { id: data.team_id, name: data.team_name },
          channel: { id: data.channel_id, name: data.channel_name },
        };
      })
    );
    // An organization with no workspaces yet has no team or channel.
    setSelectedTeamChannel(
      team && channel
        ? {
            label: `${team.name} - ${channel.name}`,
            value: channel.id,
            team: { id: team.id, name: team.name },
            channel: { id: channel.id, name: channel.name },
          }
        : null
    );
    setIsLoading(false);
  }, []);

  useEffect(() => {
    // Setting the navbar type
    if (fixedNavbar) {
      setNavbarType("sticky");
    } else {
      setNavbarType("static");
    }

    // A function that sets the transparent state of the navbar.
    function handleTransparentNavbar() {
      setTransparentNavbar(dispatch, (fixedNavbar && window.scrollY === 0) || !fixedNavbar);
    }

    /** 
     The event listener that's calling the handleTransparentNavbar function when 
     scrolling the window.
    */
    window.addEventListener("scroll", handleTransparentNavbar);

    // Call the handleTransparentNavbar function to set the state with the initial value.
    handleTransparentNavbar();

    // Remove event listener on cleanup
    return () => window.removeEventListener("scroll", handleTransparentNavbar);
  }, [dispatch, fixedNavbar]);

  const handleMiniSidenav = () => setMiniSidenav(dispatch, !miniSidenav);
  const handleConfiguratorOpen = () => setOpenConfigurator(dispatch, !openConfigurator);
  const handleOpenMenu = (event) => setOpenMenu(event.currentTarget);
  const handleCloseMenu = () => setOpenMenu(false);

  const onChangeTeamChannel = (data) => {
    setSelectedTeamChannel(data);
    reactDispatch(updateDefaultTeamChannel(data));
    navigate(`/`);
  };

  // Render the notifications menu
  const renderMenu = () => (
    <Menu
      anchorEl={openMenu}
      anchorReference={null}
      anchorOrigin={{
        vertical: "bottom",
        horizontal: "left",
      }}
      open={Boolean(openMenu)}
      onClose={handleCloseMenu}
      sx={{ mt: 2 }}
    >
      <NotificationItem
        image={<img src={team2} alt="person" />}
        title={["New message", "from Laur"]}
        date="13 minutes ago"
        onClick={handleCloseMenu}
      />
      <NotificationItem
        image={<img src={logoSpotify} alt="person" />}
        title={["New album", "by Travis Scott"]}
        date="1 day"
        onClick={handleCloseMenu}
      />
      <NotificationItem
        color="secondary"
        image={
          <Icon fontSize="small" sx={{ color: ({ palette: { white } }) => white.main }}>
            payment
          </Icon>
        }
        title={["", "Payment successfully completed"]}
        date="2 days"
        onClick={handleCloseMenu}
      />
    </Menu>
  );

  return (
    <AppBar
      position={absolute ? "absolute" : navbarType}
      color="inherit"
      sx={(theme) => navbar(theme, { transparentNavbar, absolute, light })}
    >
      <Toolbar sx={(theme) => navbarContainer(theme)}>
        <SuiBox color="inherit" mb={{ xs: 1, md: 0 }} sx={(theme) => navbarRow(theme, { isMini })}>
          <Breadcrumbs
            icon="home"
            title={
              breadcrumbTitle || (route[route.length - 1] ? route[route.length - 1] : "Dasboard")
            }
            route={route}
            light={light}
          />
        </SuiBox>
        {isMini ? null : (
          <SuiBox sx={(theme) => navbarRow(theme, { isMini })}>
            <SuiBox pr={1}>
              {/* <SuiInput
                placeholder="Type here..."
                icon={{ component: "search", direction: "left" }}
              /> */}
              {isLoading ? (
                <CircularProgress color="dark" size={18} />
              ) : (
                <CustomSelect
                  key={teamsChannels.length}
                  customStyles={customSelectStyles}
                  value={selectedTeamChannel}
                  options={teamsChannels}
                  onChange={(data) => onChangeTeamChannel(data)}
                  formatOptionLabel={(option, { context }) => (
                    <SuiBox lineHeight={1.25} sx={{ minWidth: 0, color: "inherit !important" }}>
                      <SuiBox
                        component="div"
                        sx={{
                          color: "inherit !important",
                          fontSize: 13,
                          fontWeight: 600,
                          overflowWrap: "anywhere",
                          ...(context === "value" && {
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            whiteSpace: "nowrap",
                          }),
                        }}
                      >
                        {option.channel.name}
                      </SuiBox>
                      <SuiBox
                        component="div"
                        sx={{
                          color: "inherit !important",
                          fontSize: 11,
                          opacity: 0.75,
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          whiteSpace: "nowrap",
                        }}
                      >
                        {option.team.name}
                      </SuiBox>
                    </SuiBox>
                  )}
                />
              )}
            </SuiBox>
            <SuiBox color={light ? "white" : "inherit"}>
              {/* <Link to="/profile"> */}
              <IconButton
                sx={navbarIconButton}
                size="large"
                disabled={!isLoggedIn}
                onClick={() => navigate("/profile")}
              >
                <Icon
                  sx={({ palette: { dark, white } }) => ({
                    color: light ? white.main : dark.main,
                  })}
                >
                  account_circle
                </Icon>
                <SuiTypography
                  variant="button"
                  fontWeight="medium"
                  color={light ? "white" : "dark"}
                >
                  {isLoggedIn ? admin.name : "Sign In"}
                </SuiTypography>
              </IconButton>
              {/* </Link> */}
              <IconButton
                size="medium"
                color="inherit"
                sx={navbarIconButton}
                onClick={() => {
                  reactDispatch(signout());
                }}
              >
                <Icon>logout</Icon>
              </IconButton>
              <IconButton
                size="medium"
                color="inherit"
                sx={navbarMobileMenu}
                onClick={handleMiniSidenav}
              >
                <Icon className={light ? "text-white" : "text-dark"}>
                  {miniSidenav ? "menu_open" : "menu"}
                </Icon>
              </IconButton>
              {/* <IconButton
                size="medium"
                color="inherit"
                sx={navbarIconButton}
                onClick={handleConfiguratorOpen}
              >
                <Icon>settings</Icon>
              </IconButton> */}
              {/* <IconButton
                size="medium"
                color="inherit"
                sx={navbarIconButton}
                aria-controls="notification-menu"
                aria-haspopup="true"
                variant="contained"
                onClick={handleOpenMenu}
              >
                <Icon className={light ? "text-white" : "text-dark"}>notifications</Icon>
              </IconButton> */}
              {renderMenu()}
            </SuiBox>
          </SuiBox>
        )}
      </Toolbar>
    </AppBar>
  );
}

// Setting default values for the props of DashboardNavbar
DashboardNavbar.defaultProps = {
  absolute: false,
  light: false,
  isMini: false,
  breadcrumbTitle: "",
};

// Typechecking props for the DashboardNavbar
DashboardNavbar.propTypes = {
  absolute: PropTypes.bool,
  light: PropTypes.bool,
  isMini: PropTypes.bool,
  breadcrumbTitle: PropTypes.string,
};

export default DashboardNavbar;
